import React from "react";
import { Dialog, type DialogAction } from "../Dialog";

export interface ConfirmProps {
  /**
   * Whether the confirm dialog is open
   */
  isOpen: boolean;
  /**
   * Callback when the dialog is closed without confirming
   */
  onClose: () => void;
  /**
   * Callback when the confirm button is pressed
   */
  onConfirm: () => void;
  /**
   * Dialog title
   */
  title: string;
  /**
   * Confirmation message/content
   */
  message: React.ReactNode;
  /**
   * Label for the confirm button
   * @default "Confirm"
   */
  confirmLabel?: string;
  /**
   * Label for the cancel button
   * @default "Cancel"
   */
  cancelLabel?: string;
  /**
   * Variant for the confirm button
   * @default "primary"
   */
  confirmVariant?: DialogAction["variant"];
  /**
   * Whether the confirm action is destructive (uses destructive styling)
   * @default false
   */
  isDestructive?: boolean;
  /**
   * Whether the confirm button is in a loading state
   * @default false
   */
  isLoading?: boolean;
  /**
   * Maximum width of the confirm dialog
   * @default "500px"
   */
  maxWidth?: string;
  /**
   * Minimum width of the confirm dialog (desktop only)
   * @default "400px"
   */
  minWidth?: string;
}

/**
 * Confirm
 *
 * A confirmation dialog with confirm and cancel actions.
 * Use for actions that require user confirmation before proceeding.
 */
export function Confirm({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant,
  isDestructive = false,
  isLoading = false,
  maxWidth = "500px",
  minWidth = "400px",
}: ConfirmProps) {
  const handleConfirm = () => {
    onConfirm();
    // Don't auto-close - let the parent handle closing after async operations
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxWidth={maxWidth}
      minWidth={minWidth}
      primaryAction={{
        label: confirmLabel,
        onPress: handleConfirm,
        variant: confirmVariant ?? (isDestructive ? "destructive" : "primary"),
        isLoading,
      }}
      secondaryAction={{
        label: cancelLabel,
        onPress: onClose,
        variant: "secondary",
        isDisabled: isLoading,
      }}
    >
      <div className="text-text-body">{message}</div>
    </Dialog>
  );
}
